import React, { useRef, useEffect } from 'react';
import { graphql } from 'gatsby';
import { Layout } from '@components';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion, useLanguage } from '@hooks';
import { Icon } from '@components/icons';

const StyledApplicationsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;

  header {
    margin-top: 40px;
    margin-bottom: 100px;
    text-align: center;

    h1 {
      font-size: clamp(40px, 8vw, 80px);
    }

    .subtitle {
      color: var(--green);
      font-family: var(--font-mono);
      font-size: var(--fz-md);
      font-weight: 400;
      line-height: 1.5;
      margin: 0 0 20px 0;
      max-width: 540px;
      margin-left: auto;
      margin-right: auto;

      @media (max-width: 768px) {
        margin: 0 0 30px 0;
      }
    }
  }

  .applications-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    position: relative;
    margin-top: 50px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const StyledApplication = styled.li`
  position: relative;
  cursor: pointer;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover {
      .project-inner {
        transform: translateY(-5px);
      }

      .project-links {
        opacity: 1;
      }
    }
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 350px;
    height: auto;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    overflow: hidden;

    &:hover {
      background-color: var(--lightest-navy);
      border: 2px solid var(--green);
      box-shadow: 0 0 20px rgba(100, 255, 218, 0.3);
    }
  }

  .project-image {
    width: 100%;
    height: 180px;
    flex-shrink: 0;
    overflow: hidden;
    background-color: var(--navy);
    display: flex;
    align-items: center;
    justify-content: center;

    .gatsby-image-wrapper {
      width: 100%;
      height: 100%;

      img {
        object-fit: contain;
        object-position: center;
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
      }
    }
  }

  .project-content {
    flex: 1;
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    min-height: 170px;
  }

  .project-header {
    flex: 1;
  }

  .project-links {
    position: absolute;
    top: 1rem;
    right: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    opacity: 0;
    transition: var(--transition);
    color: var(--light-slate);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 8px;
      border-radius: 4px;
      background-color: var(--navy);
      transition: var(--transition);

      &:hover {
        background-color: var(--green);
        color: var(--navy);
      }

      &.external {
        svg {
          width: 18px;
          height: 18px;
        }
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  .folder {
    display: none;
  }

  .project-title {
    margin: 8px 0 15px;
    color: var(--lightest-slate);
    font-size: var(--fz-lg);
    font-weight: 600;
    line-height: 1.4;

    &:before {
      content: '';
      display: block;
      position: absolute;
      z-index: 0;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
    }
  }

  .project-description {
    color: var(--light-slate);
    font-size: var(--fz-sm);
    line-height: 1.6;
    margin-bottom: 1.2rem;
    margin-top: 5px;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-tech-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      color: var(--green);
      background-color: rgba(100, 255, 218, 0.1);
      padding: 4px 8px;
      border-radius: 4px;
      border: 1px solid rgba(100, 255, 218, 0.2);
    }
  }

  .project-meta {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    margin-top: 8px;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    color: var(--light-slate);

    .year {
      color: var(--green);
      margin-right: 12px;
      font-weight: 500;
    }

    .category {
      color: var(--slate);
      background-color: rgba(136, 146, 176, 0.15);
      padding: 3px 8px;
      border-radius: 4px;
      font-size: var(--fz-xxs);
      font-weight: 500;
    }
  }
`;

const ApplicationPage = ({ location, data }) => {
  const { t, currentLanguage } = useLanguage();
  const applications = data.allMarkdownRemark.edges;
  const revealTitle = useRef(null);
  const revealGrid = useRef(null);
  const revealApplications = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealGrid.current, srConfig(200, 0));
    revealApplications.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const applicationInner = node => {
    const { frontmatter, html } = node;
    const { date, github, external, ios, android, tech, cover } = frontmatter;

    // 获取多语言标题和分类
    const getLocalizedText = field => {
      if (frontmatter[`${field}_${currentLanguage}`]) {
        return frontmatter[`${field}_${currentLanguage}`];
      }
      return frontmatter[field];
    };

    const localizedTitle = getLocalizedText('title');
    const localizedCategory = getLocalizedText('category');
    const localizedDescription = frontmatter[`description_${currentLanguage}`] || html;

    const handleClick = () => external && window.open(external, '_blank');
    const handleKeyDown = e => {
      if ((e.key === 'Enter' || e.key === ' ') && external) {
        e.preventDefault();
        window.open(external, '_blank');
      }
    };

    return (
      <div
        className="project-inner"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={external ? 'button' : undefined}
        tabIndex={external ? 0 : undefined}
        style={{ cursor: external ? 'pointer' : 'default' }}>
        {cover && (
          <div className="project-image">
            <img src={cover.publicURL} alt={localizedTitle} />
          </div>
        )}

        <div className="project-content">
          <div className="project-links">
            {github && (
              <a
                href={github}
                aria-label="GitHub Link"
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}>
                <Icon name="GitHub" />
              </a>
            )}
            {external && (
              <a
                href={external}
                aria-label="External Link"
                className="external"
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}>
                <Icon name="External" />
              </a>
            )}
            {ios && (
              <a
                href={ios}
                aria-label="Apple App Store Link"
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}>
                <Icon name="AppStore" />
              </a>
            )}
            {android && (
              <a
                href={android}
                aria-label="Google Play Store Link"
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}>
                <Icon name="PlayStore" />
              </a>
            )}
          </div>

          <div className="project-header">
            <div className="project-meta">
              <span className="year">{new Date(date).getFullYear()}</span>
              {localizedCategory && <span className="category">{localizedCategory}</span>}
            </div>

            <h3 className="project-title">{localizedTitle}</h3>

            <div
              className="project-description"
              dangerouslySetInnerHTML={{ __html: localizedDescription }}
            />
          </div>

          <div className="project-footer">
            {tech && (
              <ul className="project-tech-list">
                {tech.map((tech, i) => (
                  <li key={i}>{tech}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout location={location}>
      <Helmet
        title="Product Portfolio & Applications | Y Lau - Web3 & Fintech Product Manager"
        meta={[
          {
            name: 'description',
            content:
              'Explore innovative product portfolio including Web3 applications, blockchain payment solutions, cryptocurrency platforms, fintech tools, and mobility ecosystem products. Real-world case studies of successful product launches and digital transformation initiatives.',
          },
          {
            name: 'keywords',
            content:
              'product portfolio, web3 applications, blockchain payment solutions, cryptocurrency platform, fintech tools, mobility ecosystem, digital transformation, product case studies, startup applications, tech product showcase, payment gateway, OTC trading platform, charging ecosystem, product management portfolio, innovative fintech solutions, blockchain technology applications, crypto payment gateway, digital wallet, DeFi applications, product strategy examples',
          },
          {
            name: 'author',
            content: 'Y Lau',
          },
          {
            name: 'robots',
            content: 'index, follow',
          },
          {
            property: 'og:title',
            content: 'Product Portfolio & Applications | Y Lau - Web3 & Fintech Expert',
          },
          {
            property: 'og:description',
            content:
              'Explore innovative Web3 applications, blockchain payment solutions, and fintech tools. Real-world case studies of successful product launches.',
          },
          {
            property: 'og:type',
            content: 'website',
          },
          {
            property: 'og:url',
            content: typeof window !== 'undefined' ? window.location.href : '',
          },
          {
            property: 'og:image',
            content: '/og-image.png',
          },
          {
            name: 'twitter:card',
            content: 'summary_large_image',
          },
          {
            name: 'twitter:title',
            content: 'Product Portfolio & Applications | Y Lau',
          },
          {
            name: 'twitter:description',
            content:
              'Innovative Web3 applications, blockchain payment solutions, and fintech tools portfolio.',
          },
          {
            name: 'twitter:image',
            content: '/og-image.png',
          },
        ]}
      />

      <StyledApplicationsSection>
        <header ref={revealTitle}>
          <h1 className="big-heading">{t('applicationTitle')}</h1>
          <p className="subtitle">{t('applicationSubtitle')}</p>
          <a
            className="email-link"
            href="https://cwallet.com/t/ZBCJQP3N"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              border: '1px solid var(--green)',
              padding: '10px',
              borderRadius: 'var(--border-radius)',
            }}>
            {t('buyMeACoffee')}
          </a>
        </header>

        <ul className="applications-grid" ref={revealGrid}>
          {applications.length > 0 &&
            applications.map(({ node }, i) => (
              <StyledApplication key={i} ref={el => (revealApplications.current[i] = el)}>
                {applicationInner(node)}
              </StyledApplication>
            ))}
        </ul>
      </StyledApplicationsSection>
    </Layout>
  );
};

ApplicationPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default ApplicationPage;

export const pageQuery = graphql`
  {
    allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/content/applications/" } }
      sort: { frontmatter: { date: DESC } }
    ) {
      edges {
        node {
          frontmatter {
            date
            title
            title_en
            title_zh_CN
            title_zh_TW
            tech
            github
            external
            ios
            android
            category
            category_en
            category_zh_CN
            category_zh_TW
            description_en
            description_zh_CN
            description_zh_TW
            cover {
              publicURL
            }
          }
          html
        }
      }
    }
  }
`;
